import { BatchTypeEnum } from "@brewbase/db/types";
import { effectiveOutputMaterialsOf } from "./harvest-tree/harvest-tree";
export const MATERIAL_PHASE = {
    [BatchTypeEnum.BROTH]: "LIQUID",
    [BatchTypeEnum.SUPERNATANT]: "LIQUID",
    [BatchTypeEnum.CONCENTRATE]: "LIQUID",
    [BatchTypeEnum.PERMEATE]: "LIQUID",
    [BatchTypeEnum.RETENTATE]: "LIQUID",
    [BatchTypeEnum.HOMOGENATE]: "LIQUID",
    [BatchTypeEnum.POWDER]: "POWDER",
};
/**
 * What a template accepts when it says nothing.
 *
 * Also the migration's backfill value. An explicit default rather than "unset
 * means anything": the rule has to bite from the day it lands without anyone
 * editing 87 templates, and the future powder step is the one that states its
 * exception.
 */
export const DEFAULT_ACCEPTED_PHASES = ["LIQUID"];
export function isMaterialPhase(value) {
    return value === "LIQUID" || value === "POWDER";
}
/** The phase of a batch type, or null if it is not one we know. */
export function phaseOf(material) {
    return MATERIAL_PHASE[material] ?? null;
}
/** Read a stored `accepted_input_phases` column, falling back to the default. */
export function acceptedPhasesOf(stored) {
    if (!stored?.length)
        return DEFAULT_ACCEPTED_PHASES;
    const phases = stored.filter(isMaterialPhase);
    return phases.length ? phases : DEFAULT_ACCEPTED_PHASES;
}
/**
 * The materials a consumer could legitimately take across one seam.
 *
 * Empty means the seam cannot stand, and the two ways it can be empty are the
 * two failure modes: the declared stream is not produced (availability), or
 * nothing the source produces is of a phase this step accepts (compatibility).
 * Callers distinguish them by re-asking without the declaration.
 *
 * A consumer that declares nothing is not thereby invalid — its candidate set is
 * every compatible output, and a non-empty set means the seam is fine even
 * though nobody has chosen yet.
 */
export function acceptableMaterials(producerOutputs, acceptedPhases, declaredMaterial) {
    const compatible = producerOutputs.filter((material) => {
        const phase = phaseOf(material);
        return phase != null && acceptedPhases.includes(phase);
    });
    if (declaredMaterial == null)
        return compatible;
    return compatible.filter((material) => material === declaredMaterial);
}
/**
 * Which of the producer's streams this step ends up taking.
 *
 * `depends_on_material` is not a requirement the step carries around. It is
 * half of the wiring — the other half being `depends_on` — and it answers
 * *which of my source's outputs do I take*. So when the source changes it is
 * re-answered, exactly as `depends_on` is, rather than checked against the new
 * source as though it were a demand.
 *
 * Checking it as a demand is what made almost every rewire illegal: the step
 * still named the stream its old source produced, the new source does not
 * produce that, and so an ordinary move was refused for a stale answer to a
 * question nobody had re-asked.
 *
 * The answer keeps a standing choice when it is still on offer, takes the only
 * option when there is exactly one, and otherwise declines to guess — null
 * meaning "whatever comes down this line", which is what an unset column has
 * always meant.
 */
export function resolveSeamMaterial(producerOutputs, acceptedPhases, declaredMaterial) {
    const compatible = acceptableMaterials(producerOutputs, acceptedPhases, null);
    if (declaredMaterial != null && compatible.includes(declaredMaterial))
        return declaredMaterial;
    return compatible.length === 1 ? compatible[0] : null;
}
/**
 * `acceptableMaterials` for a producer node, resolving PASSTHROUGH outputs
 * through the shared rule rather than reading `outputMaterials` directly.
 */
export function seamMaterials(producer, acceptedPhases, declaredMaterial) {
    return acceptableMaterials(effectiveOutputMaterialsOf({
        kind: producer.kind ?? undefined,
        dependsOnMaterial: producer.dependsOnMaterial ?? null,
        outputMaterials: [...(producer.outputMaterials ?? [])],
    }), acceptedPhases, declaredMaterial);
}
//# sourceMappingURL=harvest-materials.js.map